import { useLocation } from 'react-router';
import { useAppSelector } from 'store/store';
import { usePermissions } from 'hooks/usePermissions';

export interface NavItem {
  label: string;
  path: string;
}

export interface MainNavigation {
  /** Lottery the user is currently working in, or `null` outside a lottery. */
  lotteryId: number | null;
  /** Entries rendered as pills in the header. */
  items: NavItem[];
  /** True when `path` is the entry the current URL belongs to. */
  isActive: (path: string) => boolean;
  canReadContent: boolean;
}

/**
 * Builds the header navigation for the signed-in user. The entries mirror the
 * Scala navigation (`views/lottery/nav.scala.html`, `views/user/nav.scala.html`)
 * and are filtered by permission.
 */
export const useMainNavigation = (): MainNavigation => {
  const location = useLocation();
  const selectedLottery = useAppSelector((state) => state.lotteries.selectedLottery);
  const user = useAppSelector((state) => state.auth.user);
  const { isSuperAdmin, isAdmin, isRedakteur, isFreigeber } = usePermissions();

  const lotteryMatch = location.pathname.match(/^\/lottery\/(\d+)/);
  const urlLotteryId = lotteryMatch ? Number(lotteryMatch[1]) : null;
  const lotteryId = urlLotteryId ?? selectedLottery?.id ?? user?.lotteryId ?? null;

  const canReadContent = isRedakteur() || isFreigeber();

  const items: NavItem[] = [];

  if (isSuperAdmin()) {
    items.push({ label: 'Lotteriebetreiber', path: '/' });
  }

  if (lotteryId) {
    if (canReadContent) {
      items.push({ label: 'Ziehungsübersicht', path: `/lottery/${lotteryId}/drawings` });
    }
    if (isRedakteur()) {
      items.push(
        { label: 'Ziehungsbasis', path: `/lottery/${lotteryId}/drawing-bases` },
        { label: 'Allgemein', path: `/lottery/${lotteryId}/general` },
      );
    }
    if (isAdmin()) {
      items.push({ label: 'Benutzerverwaltung', path: `/lottery/${lotteryId}/users` });
    }
  }

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return { lotteryId, items, isActive, canReadContent };
};
